import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Radio, MapPin, Phone, Lock, Clock, Heart, Users, Shield, Sparkles, HandHeart, Globe } from "lucide-react";
import SEO from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const FEATURES = [
  { icon: Radio, text: "Real-time connection between veterans in crisis and trained volunteer veterans" },
  { icon: MapPin, text: "Geolocation services for proximity-based matching" },
  { icon: Phone, text: "Integration and synchronization with the Veterans Crisis Hotline" },
  { icon: Lock, text: "Data encryption and background-checked volunteer network" },
  { icon: Clock, text: "Availability status system for volunteer coordination" },
];

const VALUES = [
  { icon: Heart, label: "Compassion" },
  { icon: Shield, label: "Integrity" },
  { icon: Sparkles, label: "Empowerment" },
  { icon: Users, label: "Collaboration" },
  { icon: HandHeart, label: "Resilience" },
  { icon: Globe, label: "Inclusivity" },
];

export default function Valor() {
  return (
    <div>
      {/* HERO */}
      <SEO
        title="VALOR"
        description="VALOR — Veterans' Alliance for Legislation & Organizational Reform, Inc. A 501(c)(3) nonprofit connecting veterans in crisis with trained peer volunteers through VALORLink."
        keywords="VALOR, VALORLink, veteran crisis support, veteran nonprofit, peer support, VA Crisis Hotline, Dr. Ben Magee"
        page="valor"
        canonical="https://drbenmagee.com/valor"
      />
      <section className="bg-[#1B2A4A] py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="https://images.unsplash.com/photo-1758574926737-e0615e4855d7?auto=format&w=1200" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="w-1 h-12 bg-[#C9A84C] mb-8" />
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight" data-testid="valor-hero-title">
            No Veteran Should Face a Crisis Alone
          </h1>
          <p className="mt-4 text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C]">
            VALOR &mdash; Veterans' Alliance for Legislation & Organizational Reform, Inc.
          </p>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-20 md:py-32 bg-white" data-testid="valor-overview">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Organization Overview</p>
          <div className="text-base text-[#1B2A4A]/70 leading-relaxed space-y-6">
            <p>
              Veterans' Alliance for Legislation & Organizational Reform, Inc. (VALOR) is a 501(c)(3) nonprofit headquartered in Washington, DC, founded by Dr. Ben Magee. Conceptualized in 2020, officially formed in May 2023, incorporated November 2023, and registered as a 501(c)(3) in January 2024.
            </p>
          </div>
        </div>
      </section>

      {/* VALORLINK */}
      <section className="py-20 md:py-32 bg-[#E8EDF5]" data-testid="valor-valorlink">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">The VALORLink&trade; Platform</p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight mb-6">
                Technology in Service of Veterans
              </h2>
              <div className="text-sm text-[#1B2A4A]/70 leading-relaxed space-y-4">
                <p>
                  VALORLink&trade; is VALOR's flagship technology platform &mdash; a mobile application that connects veterans in crisis with trained, compassionate fellow veteran volunteers in real time. Modeled on the efficiency of ride-sharing platforms like Uber, VALORLink&trade; provides immediate, peer-based connection that complements and synchronizes with the VA Crisis Hotline.
                </p>
                <p>
                  When a veteran reaches out, VALORLink&trade; matches them with an available veteran volunteer &mdash; someone who has lived the experience, understands the language, and can provide timely support while coordinating with professional crisis services.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              {FEATURES.map((f, i) => (
                <motion.div
                  key={f.text}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  className="flex items-start gap-3 bg-white rounded-sm p-4 border border-[#1B2A4A]/10"
                >
                  <f.icon className="h-5 w-5 text-[#C9A84C] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#1B2A4A]/70">{f.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="py-20 md:py-32 bg-white" data-testid="valor-values">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Core Values</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight mb-12">
            What Drives Us
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.label}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="text-center p-6 bg-[#E8EDF5] rounded-sm border border-[#1B2A4A]/10"
              >
                <v.icon className="h-8 w-8 text-[#C9A84C] mx-auto mb-3" />
                <p className="text-sm font-semibold text-[#1B2A4A]">{v.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTAs */}
      <section className="py-20 md:py-28 bg-[#1B2A4A]" data-testid="valor-ctas">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-4">
            Support the Mission
          </h2>
          <p className="text-sm text-white/60 mb-10 max-w-2xl mx-auto">
            Every veteran deserves to know that someone who understands is just one connection away.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://valorlinkusa.org" target="_blank" rel="noopener noreferrer">
              <Button data-testid="valor-visit-btn" className="bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white font-semibold rounded-sm px-8 py-3 h-auto text-base">
                Visit VALORLink&trade;
              </Button>
            </a>
            <Link to="/contact">
              <Button data-testid="valor-support-btn" variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-sm px-8 py-3 h-auto text-base">
                Support VALOR
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
